import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import moment from 'moment'
import { useNotificationStore } from '@/stores/helpers/notification.js'
import { useNotificationPermissionStore } from '@/stores/helpers/notificationPermission.js'
import { useAuthStore } from '@/stores/retailer/auth.js'
import i18n from '@/i18n/i18n.js'
import { errorMessage } from '@/utilities/helpers.js'
import { NOTIFICATION_TYPES } from '@/constants/constants.js'
import {
  SYSTEM_NOTIFICATION_FLOW_KINDS,
  SYSTEM_NOTIFICATION_SEVERITY_PRIORITY,
  SYSTEM_NOTIFICATION_STORAGE_KEYS,
  SYSTEM_NOTIFICATION_WELCOME_THRESHOLD
} from '@/constants/systemNotification.js'

const { t } = i18n.global

/**
 * Modal system notifications sent from the admin panel are shown in a single flow, ordered by severity priority.
 * Page specific notifications (the "İçerir" field is filled) are not part of the flow.
 */
export const isSystemNotificationFlowItem = notification =>
  notification.is_system_notification === true &&
  SYSTEM_NOTIFICATION_FLOW_KINDS.includes(notification.kind) &&
  !notification.page_url

const severityPriority = notification => {
  const index = SYSTEM_NOTIFICATION_SEVERITY_PRIORITY.indexOf(notification.type)

  return index === -1 ? SYSTEM_NOTIFICATION_SEVERITY_PRIORITY.length : index
}

const createdAtTimestamp = notification =>
  notification.created_at ? moment(notification.created_at).valueOf() : Number.MAX_SAFE_INTEGER

const sortByPriority = notifications =>
  [...notifications].sort(
    (a, b) => severityPriority(a) - severityPriority(b) || createdAtTimestamp(a) - createdAtTimestamp(b)
  )

const today = () => moment().format('YYYY-MM-DD')

const isDismissible = notification => notification.is_dismissible !== false

export const useSystemNotificationFlowStore = defineStore('retailerSystemNotificationFlow', () => {
  const notificationStore = useNotificationStore()
  const notificationPermissionStore = useNotificationPermissionStore()
  const authStore = useAuthStore()

  // State
  const queue = ref([])
  const currentIndex = ref(0)
  const isWelcomeVisible = ref(false)

  const viewedIds = new Set()
  let pendingReads = []

  // Getters
  const currentNotification = computed(() => queue.value[currentIndex.value] || null)

  const totalCount = computed(() => queue.value.length)

  const isActive = computed(() => queue.value.length > 0)

  const isFlowVisible = computed(() => !isWelcomeVisible.value && currentNotification.value !== null)

  // Closing ends the whole flow, so it is allowed only when no non-dismissible notification is left to be shown.
  const isClosable = computed(() => queue.value.slice(currentIndex.value).every(isDismissible))

  const isMultiple = computed(() => totalCount.value > 1)

  const isFirst = computed(() => currentIndex.value === 0)

  const isLast = computed(() => currentIndex.value === totalCount.value - 1)

  const severityCounts = computed(() =>
    SYSTEM_NOTIFICATION_SEVERITY_PRIORITY.map(severity => ({
      severity,
      count: queue.value.filter(notification => notification.type === severity).length
    })).filter(item => item.count > 0)
  )

  // Helpers
  const storageKey = key => `${key}:${authStore.user?.id}`

  const isDateToday = key => localStorage.getItem(storageKey(key)) === today()

  const saveTodayFor = key => localStorage.setItem(storageKey(key), today())

  const postponedIdsForToday = () => {
    const postponed = JSON.parse(localStorage.getItem(storageKey(SYSTEM_NOTIFICATION_STORAGE_KEYS.POSTPONED)) || 'null')

    return postponed?.date === today() ? postponed.ids : []
  }

  /**
   * Only the notifications in the queue at that moment are postponed for today; they stay unread, so they are
   * shown again the next day. Notifications arriving later in the day are still shown.
   */
  const savePostponed = notifications =>
    localStorage.setItem(
      storageKey(SYSTEM_NOTIFICATION_STORAGE_KEYS.POSTPONED),
      JSON.stringify({
        date: today(),
        ids: [...postponedIdsForToday(), ...notifications.map(notification => notification.id)]
      })
    )

  // Actions
  const markAsRead = async notification => {
    try {
      await authStore.markAsReadNotification(notification.id)
    } catch (err) {
      notificationStore.add({
        type: 'danger',
        message:
          t('store.shared.update_error', { element: t('components.system_notification_modal.notification') }) +
          errorMessage(err)
      })
      throw err
    }
  }

  const markAsViewed = notification => {
    if (!notification || viewedIds.has(notification.id)) {
      return
    }

    viewedIds.add(notification.id)
    // The error is already notified in markAsRead; a failed read must not break the flow.
    pendingReads.push(markAsRead(notification).catch(() => {}))
  }

  // Dismissible notifications are read once they are shown.
  const markCurrentAsViewed = () => {
    if (currentNotification.value && isDismissible(currentNotification.value)) {
      markAsViewed(currentNotification.value)
    }
  }

  // Non-dismissible notifications are read only after the user moves past them with the footer button.
  const acknowledgeCurrent = () => {
    markAsViewed(currentNotification.value)
  }

  const enqueue = notifications => {
    // Only the queue is checked, so notifications removed unread from the store are queued again when re-added.
    const queuedIds = queue.value.map(notification => notification.id)
    const incoming = notifications.filter(notification => !queuedIds.includes(notification.id))

    if (!incoming.length) {
      return
    }

    const postponedIds = postponedIdsForToday()
    const isPostponed = notification => postponedIds.includes(notification.id)
    const postponed = incoming.filter(isPostponed)

    if (postponed.length) {
      notificationStore.removeMany(postponed)
    }

    const visible = incoming.filter(notification => !isPostponed(notification))

    if (!visible.length) {
      return
    }

    if (isWelcomeVisible.value) {
      queue.value = sortByPriority([...queue.value, ...visible])
      return
    }

    if (queue.value.length) {
      // Notifications arriving during the flow are re-sorted together with the ones not shown yet.
      const shown = queue.value.slice(0, currentIndex.value + 1)
      const upcoming = queue.value.slice(currentIndex.value + 1)

      queue.value = [...shown, ...sortByPriority([...upcoming, ...visible])]
      return
    }

    queue.value = sortByPriority(visible)
    currentIndex.value = 0

    if (
      queue.value.length >= SYSTEM_NOTIFICATION_WELCOME_THRESHOLD &&
      !isDateToday(SYSTEM_NOTIFICATION_STORAGE_KEYS.WELCOME_SHOWN_DATE)
    ) {
      isWelcomeVisible.value = true
      saveTodayFor(SYSTEM_NOTIFICATION_STORAGE_KEYS.WELCOME_SHOWN_DATE)
      return
    }

    markCurrentAsViewed()
  }

  const review = () => {
    isWelcomeVisible.value = false
    markCurrentAsViewed()
  }

  const finish = async () => {
    acknowledgeCurrent()

    const shouldReload = queue.value.some(
      notification => notification.kind === NOTIFICATION_TYPES.REFRESH && viewedIds.has(notification.id)
    )

    notificationStore.removeMany(queue.value)
    viewedIds.clear()
    queue.value = []
    currentIndex.value = 0
    isWelcomeVisible.value = false
    notificationPermissionStore.updateSystemNotificationApproval(true)

    if (shouldReload) {
      await Promise.allSettled(pendingReads)
      pendingReads = []
      window.location.reload()
    }
  }

  const postpone = () => {
    // Non-dismissible notifications cannot be postponed.
    const dismissible = queue.value.filter(isDismissible)

    savePostponed(dismissible)
    notificationStore.removeMany(dismissible)
    isWelcomeVisible.value = false
    queue.value = queue.value.filter(notification => !isDismissible(notification))
    currentIndex.value = 0

    if (!queue.value.length) {
      return finish()
    }

    markCurrentAsViewed()
  }

  const next = () => {
    acknowledgeCurrent()

    if (isLast.value) {
      return finish()
    }

    currentIndex.value++
    markCurrentAsViewed()
  }

  const previous = () => {
    if (!isFirst.value) {
      currentIndex.value--
    }
  }

  return {
    queue,
    currentIndex,
    isWelcomeVisible,
    currentNotification,
    totalCount,
    isActive,
    isFlowVisible,
    isClosable,
    isMultiple,
    isFirst,
    isLast,
    severityCounts,
    enqueue,
    review,
    postpone,
    next,
    previous,
    finish
  }
})
